import React, { useCallback, useEffect, useState } from "react";
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import ModalWidget from "../ModalWidget";
import { ReactComponent as ImportBorderSvg } from "../../../assets/svg/import.svg";
import { capitalizeFirstLetter } from "../../../services/HelperService";
import type { ColumnMapping } from "./ImportWizard";
import FieldChip from "./FieldChip";
import ColumnDropZone from "./ColumnDropZone";

export interface FieldDef {
    key: string;
    label: string;
    required: boolean;
}

interface ImportColumnMapperProps {
    userType: string;
    headers: string[];
    previewData: string[][];
    fields: FieldDef[];
    onBack: () => void;
    onDismiss?: () => void;
    onClose?: () => void;
    onMappingComplete: (mapping: ColumnMapping, manualFields: string[]) => void;
    lockedClassroomName?: string;
}

/** Fuzzy-match a column header to a field key */
function autoMapHeaders(headers: string[], fields: FieldDef[]): ColumnMapping {
    const mapping: ColumnMapping = {};
    const usedFields = new Set<string>();

    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

    const aliasMap: Record<string, string[]> = {
        first_name: ["firstname", "fname", "first"],
        last_name: ["lastname", "lname", "last", "surname"],
        email: ["email", "emailaddress", "mail"],
        phone_no: ["phone", "phoneno", "phonenumber", "tel", "telephone", "mobile"],
        gender: ["gender", "sex"],
        dob: ["dob", "dateofbirth", "birthdate", "birthday"],
        guardian_first_name: ["guardianfirstname", "parentfirstname", "guardfirst"],
        guardian_last_name: ["guardianlastname", "parentlastname", "guardlast"],
        guardian_email: ["guardianemail", "parentemail"],
        guardian_phone: ["guardianphone", "parentphone"],
        guardian_relationship_type: ["guardianrelationship", "guardianrelationshiptype", "parentrelationship", "relationship"],
        class_name: ["class", "classname", "classroom", "grade"],
        student_email: ["studentemail"],
        student_relationship_type: ["studentrelationship", "studentrelationshiptype", "wardrelationship"],
    };

    headers.forEach((header, idx) => {
        const norm = normalize(header);
        for (const field of fields) {
            if (usedFields.has(field.key)) continue;
            const aliases = aliasMap[field.key] || [normalize(field.key)];
            if (aliases.some(a => norm === a || norm.includes(a))) {
                mapping[idx] = [field.key];
                usedFields.add(field.key);
                break;
            }
        }
    });

    return mapping;
}

function ImportColumnMapper({
    userType,
    headers,
    previewData,
    fields,
    onBack,
    onDismiss,
    onClose,
    onMappingComplete,
    lockedClassroomName,
}: ImportColumnMapperProps) {
    const capitalWord = capitalizeFirstLetter(userType);
    const [mapping, setMapping] = useState<ColumnMapping>({});
    const [manualFields, setManualFields] = useState<Set<string>>(new Set());
    const [activeField, setActiveField] = useState<string | null>(null);
    const [selectedField, setSelectedField] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
        useSensor(KeyboardSensor)
    );

    // When classroom is locked, exclude class_name from user-mappable fields
    const availableFields = lockedClassroomName
        ? fields.filter(f => f.key !== "class_name")
        : fields;

    // Auto-map on mount only
    useEffect(() => {
        setMapping(autoMapHeaders(headers, availableFields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fields already mapped (to a column or marked manual)
    const mappedFields = new Set(Object.values(mapping).flat());

    // Unmapped fields (available in tray) — exclude manual fields and locked fields
    const unmappedFields = availableFields.filter(f => !mappedFields.has(f.key) && !manualFields.has(f.key));

    // Check if all required fields are mapped or marked manual
    const requiredFields = availableFields.filter(f => f.required);
    const allRequiredSatisfied = requiredFields.every(f => mappedFields.has(f.key) || manualFields.has(f.key));

    // Unmapped required fields that could be set to manual
    const unmappedRequiredFields = requiredFields.filter(f => !mappedFields.has(f.key) && !manualFields.has(f.key));

    const toggleManualField = (fieldKey: string) => {
        setManualFields(prev => {
            const next = new Set(prev);
            if (next.has(fieldKey)) {
                next.delete(fieldKey);
            } else {
                next.add(fieldKey);
            }
            return next;
        });
    };

    /** Place a field into a column (shared by drag-end and tap-to-place) */
    const placeField = useCallback((fieldKey: string, colIndex: number) => {
        setMapping(prev => {
            const newMapping = { ...prev };
            // Remove this field from any previous column
            for (const [k, v] of Object.entries(newMapping)) {
                const idx = parseInt(k);
                if (Array.isArray(v) && v.includes(fieldKey)) {
                    newMapping[idx] = v.filter(f => f !== fieldKey);
                    if (newMapping[idx].length === 0) delete newMapping[idx];
                }
            }
            // Append to target column
            newMapping[colIndex] = [...(newMapping[colIndex] || []), fieldKey];
            return newMapping;
        });
        // Remove from manual if it was there
        setManualFields(prev => { const next = new Set(prev); next.delete(fieldKey); return next; });
    }, []);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveField(event.active.id as string);
        setSelectedField(null); // Clear tap selection when dragging
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveField(null);
        const fieldKey = event.active.id as string;
        const overId = event.over?.id as string | undefined;

        if (!overId || !overId.startsWith("col-")) return;

        const colIndex = parseInt(overId.replace("col-", ""), 10);
        if (isNaN(colIndex)) return;

        placeField(fieldKey, colIndex);
    };

    /** Tap-to-select: tap a field chip, then tap a column to place it */
    const handleFieldTap = (fieldKey: string) => {
        setSelectedField(prev => prev === fieldKey ? null : fieldKey);
    };

    const handleColumnTap = (colIndex: number) => {
        if (!selectedField) return;
        placeField(selectedField, colIndex);
        setSelectedField(null);
    };

    const removeFieldFromColumn = (colIndex: number, fieldKey: string) => {
        const newMapping = { ...mapping };
        const arr = (newMapping[colIndex] || []).filter(f => f !== fieldKey);
        if (arr.length === 0) {
            delete newMapping[colIndex];
        } else {
            newMapping[colIndex] = arr;
        }
        setMapping(newMapping);
    };

    const activeFieldDef = activeField ? fields.find(f => f.key === activeField) : null;

    return (
        <ModalWidget
            headerIcon={ImportBorderSvg}
            show={true}
            onHide={onDismiss ?? onClose}
            onBack={onBack}
            title={`Map ${capitalWord} Columns`}
            description="Drag fields onto column headers to map them. You can drop multiple fields on one column — values will be split by spaces."
            actionTitle="Continue"
            onAction={() => onMappingComplete(mapping, Array.from(manualFields))}
            actionDisabled={!allRequiredSatisfied}
            navigateBack={false}
            backButtonTitle="Back"
        >
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                {/* Field Chip Tray */}
                <div className="mb-3">
                    <p className="font-xs font-bold mb-1">Available Fields</p>
                    <p className="font-xs text-muted mb-2" style={{ fontSize: "0.65rem" }}>Drag fields onto columns, or tap a field then tap a column to place it.</p>
                    <div className="d-flex flex-wrap gap-2">
                        {unmappedFields.map(field => (
                            <FieldChip
                                key={field.key}
                                id={field.key}
                                label={field.label}
                                required={field.required}
                                isSelected={selectedField === field.key}
                                onTap={handleFieldTap}
                            />
                        ))}
                        {unmappedFields.length === 0 && (
                            <span className="text-muted font-xs">All fields mapped</span>
                        )}
                    </div>
                </div>

                {/* Column Mapping Table */}
                <div style={{ overflowX: "auto", maxHeight: 400 }}>

                    {lockedClassroomName && (
                        <div className="alert alert-info py-2 font-xs mb-2">
                            <strong>Class:</strong> All students will be assigned to <strong>{lockedClassroomName}</strong>
                        </div>
                    )}

                    <table className="table table-sm table-bordered" style={{ fontSize: "0.8rem" }}>
                        <thead>
                            <tr>
                                {headers.map((header, idx) => (
                                    <th key={idx} style={{ minWidth: 140, verticalAlign: "top" }}>
                                        <ColumnDropZone
                                            id={`col-${idx}`}
                                            header={header}
                                            mappedFields={(mapping[idx] || []).map(k => fields.find(f => f.key === k)!).filter(Boolean)}
                                            onRemoveField={(fieldKey: string) => removeFieldFromColumn(idx, fieldKey)}
                                            hasSelectedField={!!selectedField}
                                            onTapPlace={() => handleColumnTap(idx)}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {previewData.map((row, rIdx) => (
                                <tr key={rIdx}>
                                    {headers.map((_, cIdx) => (
                                        <td key={cIdx} className="text-truncate" style={{ maxWidth: 160 }}>
                                            {row[cIdx] ?? ""}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Drag Overlay */}
                <DragOverlay>
                    {activeFieldDef && (
                        <div
                            style={{
                                padding: "4px 12px",
                                borderRadius: 20,
                                backgroundColor: activeFieldDef.required ? "#4f46e5" : "#6b7280",
                                color: "#fff",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            }}
                        >
                            {activeFieldDef.label}
                        </div>
                    )}
                </DragOverlay>
            </DndContext>

            {/* Manual entry fields */}
            {manualFields.size > 0 && (
                <div className="alert alert-info mt-3 py-2 font-xs mb-0">
                    <strong>Manual entry:</strong> {fields.filter(f => manualFields.has(f.key)).map(f => f.label).join(", ")}
                    {" — you'll fill these in the next step."}
                </div>
            )}

            {unmappedRequiredFields.length > 0 && (
                <div className="alert alert-warning mt-3 py-2 font-xs mb-0">
                    <div className="mb-2">
                        Unmapped required fields: {unmappedRequiredFields.map(f => f.label).join(", ")}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {unmappedRequiredFields.map(f => (
                            <button
                                key={f.key}
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                style={{ fontSize: "0.7rem" }}
                                onClick={() => toggleManualField(f.key)}
                            >
                                Fill "{f.label}" manually
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </ModalWidget>
    );
}

export default ImportColumnMapper;
